/**
 * LeadCaptureModal — "Try Live Demo" signup flow on the public Landing page.
 *
 * Two-step UX:
 *   1) Form (name, work email, company, phone, property_count, city/state)
 *      → POST /api/leads/demo-signup → server provisions an isolated demo org.
 *   2) Reveal — shows the demo credentials + a "Sign into your demo" button
 *      that auto-logs in as the demo Property Manager, then routes to the PM
 *      dashboard so the prospect walks through their own sandbox instantly.
 */
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Badge } from './ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Rocket, Copy, LogIn, ShieldCheck, Clock, Layers } from 'lucide-react';
import { toast } from 'sonner';
import { packageDetails } from '../lib/packageDetails';
import ZipSuggestions from './common/ZipSuggestions';

const API = process.env.REACT_APP_BACKEND_URL;

const ROLE_LABEL = {
  property_manager: 'Property Manager',
  supervisor: 'Supervisor',
  crew_leader: 'Crew Leader',
  agent: 'Site Agent',
  tenant: 'Resident',
};

// The 6 official Collections — kept in tier order so the dropdown reads as
// an upsell ladder. Sourced from packageDetails.js at module load so any
// future tier addition automatically shows up in the demo picker.
const COLLECTION_OPTIONS = Object.keys(packageDetails);

export default function LeadCaptureModal({ open, onOpenChange }) {
  const navigate = useNavigate();
  const [step, setStep] = useState('form'); // form | reveal
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState({
    name: '',
    work_email: '',
    company: '',
    phone: '',
    property_count: '',
    city: '',
    state: 'TX',
    // NEW (Feb 2026) — visitor-supplied property metrics so their demo
    // sandbox is seeded with realistic numbers instead of the old hard-coded
    // 50-units / 1-building placeholders. All three fall back safely
    // server-side when left blank.
    property_name: '',
    address: '',
    zip_code: '',
    total_units: '',
    total_buildings: '',
    // Feb 2026 — preferred Collection tier. Seeds the demo property's
    // `package_type` and auto-enables the correct Specialty Services.
    package_type: '',
    notes: '',
  });
  const [creds, setCreds] = useState([]);
  const [meta, setMeta] = useState({ lead_id: '', expires_at: '' });

  const setField = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e?.preventDefault?.();
    if (!form.name || !form.work_email || !form.company) {
      toast.error('Please fill name, work email, and company.');
      return;
    }
    setLoading(true);
    try {
      const res = await fetch(`${API}/api/leads/demo-signup`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          ...form,
          property_count: form.property_count ? Number(form.property_count) : undefined,
          total_units: form.total_units ? Number(form.total_units) : undefined,
          total_buildings: form.total_buildings ? Number(form.total_buildings) : undefined,
          property_name: form.property_name || undefined,
          address: form.address || undefined,
          zip_code: form.zip_code || undefined,
          package_type: form.package_type || undefined,
        }),
      });
      const data = await res.json();
      if (!res.ok) {
        toast.error(data.detail || 'Something went wrong');
        return;
      }
      setCreds(data.demo_credentials || []);
      setMeta({ lead_id: data.lead_id, expires_at: data.expires_at });
      setStep('reveal');
      toast.success(data.reused ? 'Welcome back — your demo is still active.' : 'Your demo environment is live!');
    } catch (err) {
      toast.error('Network error — please try again.');
    } finally {
      setLoading(false);
    }
  };

  const primaryCred = creds.find((c) => c.primary) || creds.find((c) => c.role === 'property_manager');

  const autoLoginAsPM = async () => {
    if (!primaryCred) return;
    setLoading(true);
    try {
      const res = await fetch(`${API}/api/auth/login`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: primaryCred.email, password: primaryCred.password }),
      });
      const data = await res.json();
      if (!res.ok) {
        toast.error(data.detail || 'Auto sign-in failed');
        return;
      }
      // Persist token for the AuthContext bootstrap
      try { localStorage.setItem('session_token', data.session_token); } catch (_) { /* ignore */ }
      onOpenChange?.(false);
      navigate('/property-manager-dashboard');
      window.location.reload(); // ensure AuthContext refetches /me with the fresh cookie
    } catch (err) {
      toast.error('Auto sign-in failed.');
    } finally {
      setLoading(false);
    }
  };

  const copy = (t) => {
    try { navigator.clipboard?.writeText(t); toast.success('Copied'); } catch (_) { /* ignore */ }
  };

  const reset = () => {
    setStep('form');
    setCreds([]);
    setMeta({ lead_id: '', expires_at: '' });
    setForm({
      name: '', work_email: '', company: '', phone: '',
      property_count: '', city: '', state: 'TX',
      property_name: '', address: '', zip_code: '',
      total_units: '', total_buildings: '',
      package_type: '',
      notes: '',
    });
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(v) => { onOpenChange?.(v); if (!v) reset(); }}
    >
      <DialogContent
        className="max-w-2xl bg-zinc-950 border-2 border-[#C9A962]/60 text-white"
        data-testid="lead-capture-modal"
      >
        <DialogHeader>
          <DialogTitle className="text-2xl flex items-center gap-2" style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>
            <Rocket className="h-6 w-6 text-[#C9A962]" />
            {step === 'form' ? 'TRY THE LIVE NECS DEMO' : 'YOUR DEMO IS READY'}
          </DialogTitle>
          <DialogDescription className="text-zinc-400">
            {step === 'form'
              ? 'We spin up your own private sandbox in 30 seconds — 2 sample properties, all 5 roles, 7-day preview.'
              : 'Sign in below to walk your team through the full NECS experience.'}
          </DialogDescription>
        </DialogHeader>

        {step === 'form' && (
          <>
            <form onSubmit={submit} className="space-y-4" data-testid="lead-capture-form">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div>
                <Label htmlFor="lead-name" className="text-zinc-300">Full name *</Label>
                <Input
                  id="lead-name"
                  value={form.name}
                  onChange={(e) => setField('name', e.target.value)}
                  placeholder="Alex Rivera"
                  className="bg-zinc-900 border-zinc-700 text-white"
                  data-testid="lead-input-name"
                  required
                />
              </div>
              <div>
                <Label htmlFor="lead-email" className="text-zinc-300">Work email *</Label>
                <Input
                  id="lead-email"
                  type="email"
                  value={form.work_email}
                  onChange={(e) => setField('work_email', e.target.value)}
                  placeholder="alex@yourcompany.com"
                  className="bg-zinc-900 border-zinc-700 text-white"
                  data-testid="lead-input-email"
                  required
                />
              </div>
              <div>
                <Label htmlFor="lead-company" className="text-zinc-300">Company *</Label>
                <Input
                  id="lead-company"
                  value={form.company}
                  onChange={(e) => setField('company', e.target.value)}
                  placeholder="Acme Realty"
                  className="bg-zinc-900 border-zinc-700 text-white"
                  data-testid="lead-input-company"
                  required
                />
              </div>
              <div>
                <Label htmlFor="lead-phone" className="text-zinc-300">Phone</Label>
                <Input
                  id="lead-phone"
                  value={form.phone}
                  onChange={(e) => setField('phone', e.target.value)}
                  placeholder="Your mobile number"
                  className="bg-zinc-900 border-zinc-700 text-white"
                  data-testid="lead-input-phone"
                />
              </div>
              <div>
                <Label htmlFor="lead-props" className="text-zinc-300"># Properties</Label>
                <Input
                  id="lead-props"
                  type="number"
                  min="1"
                  value={form.property_count}
                  onChange={(e) => setField('property_count', e.target.value)}
                  placeholder="12"
                  className="bg-zinc-900 border-zinc-700 text-white"
                  data-testid="lead-input-property-count"
                />
              </div>
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <Label htmlFor="lead-city" className="text-zinc-300">City</Label>
                  <Input
                    id="lead-city"
                    value={form.city}
                    onChange={(e) => setField('city', e.target.value)}
                    placeholder="Austin"
                    className="bg-zinc-900 border-zinc-700 text-white"
                    data-testid="lead-input-city"
                  />
                </div>
                <div>
                  <Label htmlFor="lead-state" className="text-zinc-300">State</Label>
                  <Input
                    id="lead-state"
                    value={form.state}
                    onChange={(e) => setField('state', e.target.value.toUpperCase().slice(0, 2))}
                    placeholder="TX"
                    className="bg-zinc-900 border-zinc-700 text-white"
                    data-testid="lead-input-state"
                  />
                </div>
              </div>
            </div>

            {/* Property-specific metrics — used to seed the demo sandbox with
                the visitor's own numbers instead of the old 50/1 defaults. */}
            <div className="rounded-md border border-zinc-800 bg-zinc-900/50 p-3 space-y-3">
              <p className="text-[10px] uppercase tracking-widest text-zinc-500">
                Your flagship property (optional — we&apos;ll seed the demo with these)
              </p>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                <div className="md:col-span-3">
                  <Label htmlFor="lead-package" className="text-zinc-300 flex items-center gap-1">
                    <Layers className="h-3.5 w-3.5 text-[#C9A962]" />
                    Preferred Service Collection
                  </Label>
                  <Select
                    value={form.package_type}
                    onValueChange={(v) => setField('package_type', v)}
                  >
                    <SelectTrigger
                      id="lead-package"
                      className="bg-zinc-900 border-zinc-700 text-white"
                      data-testid="lead-input-package"
                    >
                      <SelectValue placeholder="Pick a Collection so we auto-enable the right services…" />
                    </SelectTrigger>
                    <SelectContent>
                      {COLLECTION_OPTIONS.map((name) => (
                        <SelectItem key={name} value={name} data-testid={`lead-package-option-${name.replace(/\s+/g, '-')}`}>
                          <div className="flex flex-col text-left">
                            <span className="text-sm font-semibold">{name}</span>
                            <span className="text-[11px] text-zinc-500">
                              {(packageDetails[name]?.designedFor || '').slice(0, 60)}
                            </span>
                          </div>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  {form.package_type && packageDetails[form.package_type] && (
                    <p className="text-[11px] text-zinc-400 mt-1" data-testid="lead-package-description">
                      {packageDetails[form.package_type].designedFor}
                    </p>
                  )}
                </div>
                <div>
                  <Label htmlFor="lead-prop-name" className="text-zinc-300">Property name</Label>
                  <Input
                    id="lead-prop-name"
                    value={form.property_name}
                    onChange={(e) => setField('property_name', e.target.value)}
                    placeholder="Sunrise Apartments"
                    className="bg-zinc-900 border-zinc-700 text-white"
                    data-testid="lead-input-property-name"
                    maxLength={80}
                  />
                </div>
                <div>
                  <Label htmlFor="lead-total-units" className="text-zinc-300">Total units</Label>
                  <Input
                    id="lead-total-units"
                    type="number"
                    min="1"
                    value={form.total_units}
                    onChange={(e) => setField('total_units', e.target.value)}
                    placeholder="180"
                    className="bg-zinc-900 border-zinc-700 text-white"
                    data-testid="lead-input-total-units"
                  />
                </div>
                <div>
                  <Label htmlFor="lead-total-buildings" className="text-zinc-300">Buildings</Label>
                  <Input
                    id="lead-total-buildings"
                    type="number"
                    min="1"
                    value={form.total_buildings}
                    onChange={(e) => setField('total_buildings', e.target.value)}
                    placeholder="6"
                    className="bg-zinc-900 border-zinc-700 text-white"
                    data-testid="lead-input-total-buildings"
                  />
                </div>
                <div className="md:col-span-2">
                  <Label htmlFor="lead-address" className="text-zinc-300">Street address</Label>
                  <Input
                    id="lead-address"
                    value={form.address}
                    onChange={(e) => setField('address', e.target.value)}
                    placeholder="123 Sunrise Blvd"
                    className="bg-zinc-900 border-zinc-700 text-white"
                    data-testid="lead-input-address"
                    maxLength={200}
                  />
                </div>
                <div>
                  <Label htmlFor="lead-zip" className="text-zinc-300">Zip code</Label>
                  <Input
                    id="lead-zip"
                    value={form.zip_code}
                    onChange={(e) => setField('zip_code', e.target.value)}
                    placeholder="76017"
                    className="bg-zinc-900 border-zinc-700 text-white"
                    data-testid="lead-input-zip"
                    maxLength={12}
                  />
                  <ZipSuggestions
                    city={form.city}
                    state={form.state}
                    currentZip={form.zip_code}
                    onPick={(z) => setField('zip_code', z)}
                    testid="lead-zip-suggestions"
                  />
                </div>
              </div>
            </div>

            <div className="flex items-center gap-2 text-xs text-zinc-500">
              <ShieldCheck className="h-4 w-4 text-emerald-500" />
              We never share your info. Your demo auto-expires in 7 days.
            </div>

            <div className="flex gap-3 justify-end pt-2">
              <Button
                type="button"
                variant="outline"
                onClick={() => onOpenChange?.(false)}
                className="border-zinc-700 text-zinc-300"
                data-testid="lead-cancel-btn"
              >
                Cancel
              </Button>
              <Button
                type="submit"
                disabled={loading}
                className="bg-[#C9A962] hover:bg-[#b89651] text-black font-bold"
                data-testid="lead-submit-btn"
              >
                <Rocket className="h-4 w-4 mr-2" />
                {loading ? 'Provisioning…' : 'LAUNCH MY LIVE DEMO'}
              </Button>
            </div>
          </form>
          </>
        )}

        {step === 'reveal' && (
          <div className="space-y-4" data-testid="lead-reveal">
            <div className="bg-emerald-900/30 border border-emerald-700 rounded-lg p-3 text-emerald-200 text-sm flex items-center gap-2">
              <Clock className="h-4 w-4" />
              Demo expires <strong className="ml-1">{meta.expires_at ? new Date(meta.expires_at).toLocaleString() : 'in 7 days'}</strong>
            </div>

            {primaryCred && (
              <div className="bg-zinc-900 border border-[#C9A962]/60 rounded-lg p-4">
                <div className="flex items-center justify-between mb-2">
                  <Badge className="bg-[#C9A962] text-black">Property Manager — Primary</Badge>
                  <span className="text-xs text-zinc-500">Recommended entry point</span>
                </div>
                <div className="text-white font-semibold">{primaryCred.name}</div>
                <div className="mt-2 grid grid-cols-1 gap-2 text-sm">
                  <div className="flex items-center gap-2">
                    <span className="text-zinc-400 w-16">Email:</span>
                    <span className="font-mono text-zinc-200 break-all">{primaryCred.email}</span>
                    <button onClick={() => copy(primaryCred.email)} className="text-zinc-400 hover:text-[#C9A962]" title="Copy">
                      <Copy className="h-3 w-3" />
                    </button>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="text-zinc-400 w-16">Password:</span>
                    <span className="font-mono text-emerald-300">{primaryCred.password}</span>
                    <button onClick={() => copy(primaryCred.password)} className="text-zinc-400 hover:text-[#C9A962]" title="Copy">
                      <Copy className="h-3 w-3" />
                    </button>
                  </div>
                </div>
                <Button
                  onClick={autoLoginAsPM}
                  disabled={loading}
                  className="mt-3 w-full bg-[#C9A962] hover:bg-[#b89651] text-black font-bold"
                  data-testid="lead-auto-login-btn"
                >
                  <LogIn className="h-4 w-4 mr-2" />
                  {loading ? 'Signing in…' : 'SIGN INTO MY DEMO NOW'}
                </Button>
              </div>
            )}

            <div>
              <div className="text-xs uppercase tracking-widest text-zinc-400 mb-2">Also seeded — other role logins</div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-2 max-h-56 overflow-y-auto pr-1">
                {creds.filter((c) => !c.primary && c.role !== 'property_manager').map((c) => (
                  <div key={c.email} className="bg-zinc-900 border border-zinc-800 rounded-md p-2 text-xs" data-testid={`lead-cred-${c.role}`}>
                    <div className="flex items-center justify-between">
                      <Badge className="bg-zinc-800 text-zinc-200 text-[10px]">{ROLE_LABEL[c.role] || c.role}</Badge>
                      {c.badge && <span className="font-mono text-zinc-400">{c.badge}</span>}
                    </div>
                    <div className="mt-1 font-mono text-zinc-200 break-all flex items-center gap-1">
                      {c.email}
                      <button onClick={() => copy(c.email)} className="text-zinc-500 hover:text-[#C9A962]" title="Copy">
                        <Copy className="h-3 w-3" />
                      </button>
                    </div>
                    <div className="text-zinc-500">password: <span className="text-emerald-300 font-mono">{c.password}</span></div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
